import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ActivityIndicator,
  Platform,
  TouchableOpacity,
  Share,
  LayoutChangeEvent,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import QRCode from 'react-native-qrcode-svg';
import ViewShot from 'react-native-view-shot';
import { colors, spacing, borderRadius } from '@tipsy/design-system';
import { apiClient } from '../api';

declare const window: {
  location: { origin: string };
  print: () => void;
} | undefined;

declare const document: {
  getElementById: (id: string) => HTMLElement | null;
  createElement: (tag: string) => HTMLElement & { id: string; textContent: string };
  head: { appendChild: (el: HTMLElement) => void };
} | undefined;

const getTipBaseUrl = (): string => {
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    return `${window.location.origin}/tip`;
  }
  return __DEV__ ? 'http://localhost:5173/tip' : 'https://tipsy.swiftly.bz/tip';
};

const injectPrintStyles = () => {
  if (Platform.OS !== 'web' || typeof document === 'undefined') return;

  const styleId = 'qr-print-styles';
  if (document.getElementById(styleId)) return;

  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `
    @media print {
      body * { visibility: hidden; }
      #qr-print-area, #qr-print-area * { visibility: visible; }
      #qr-print-area {
        position: absolute;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
      }
      .no-print { display: none !important; }
    }
  `;
  document.head.appendChild(style);
};

interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
}

const QRCodeScreen = () => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [containerWidth, setContainerWidth] = useState(0);
  const viewShotRef = useRef<ViewShot>(null);

  const qrSize = containerWidth > 0 ? containerWidth * 0.8 : 250;

  const handleLayout = (event: LayoutChangeEvent) => {
    const { width } = event.nativeEvent.layout;
    setContainerWidth(width);
  };

  useEffect(() => {
    injectPrintStyles();
  }, []);

  const handleShare = async () => {
    if (Platform.OS === 'web') return;

    try {
      const uri = await viewShotRef.current?.capture?.();
      if (uri) {
        await Share.share({
          url: uri,
          message: 'Scan this QR code to tip me!',
        });
      }
    } catch {
      // Handle error silently
    }
  };

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await apiClient.get<User>('/users/me');
        setUser(response.data);
      } catch {
        // Handle error silently
      } finally {
        setIsLoading(false);
      }
    };
    fetchUser();
  }, []);

  const handlePrint = () => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      window.print();
    }
  };

  if (isLoading || !user?.id) {
    return (
      <SafeAreaView style={styles.container} edges={['bottom']}>
        <View style={styles.content}>
          <ActivityIndicator size="large" color={colors.interactive.primary} />
        </View>
      </SafeAreaView>
    );
  }

  const qrData = `${getTipBaseUrl()}/${user.id}`;

  console.log('QR Data:', qrData);

  const qrContent = (
    <View style={styles.qrContainer}>
      <QRCode value={qrData} size={qrSize} backgroundColor="white" color="black" />
    </View>
  );

  return (
    <SafeAreaView style={styles.container} edges={['bottom']}>
      <View style={styles.content} onLayout={handleLayout}>
        {Platform.OS === 'web' ? (
          <View id="qr-print-area">{qrContent}</View>
        ) : (
          <ViewShot ref={viewShotRef} options={{ format: 'png', quality: 1 }}>
            {qrContent}
          </ViewShot>
        )}
        <Text style={styles.instruction}>Scan to tip</Text>
        {Platform.OS === 'web' ? (
          <TouchableOpacity style={styles.actionButton} onPress={handlePrint}>
            <Text style={styles.actionButtonText}>Print QR Code</Text>
          </TouchableOpacity>
        ) : (
          <TouchableOpacity style={styles.actionButton} onPress={handleShare}>
            <Text style={styles.actionButtonText}>Share QR Code</Text>
          </TouchableOpacity>
        )}
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background.primary,
  },
  content: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: spacing.base,
  },
  qrContainer: {
    padding: spacing.lg,
    backgroundColor: 'white',
    borderRadius: 16,
  },
  instruction: {
    marginTop: spacing.lg,
    fontSize: 18,
    fontWeight: '600',
    color: colors.text.primary,
    textAlign: 'center',
  },
  actionButton: {
    marginTop: spacing.xl,
    backgroundColor: '#000000',
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.xl,
    borderRadius: borderRadius.lg,
  },
  actionButtonText: {
    color: '#FFDD00',
    fontSize: 16,
    fontWeight: '600',
  },
});

export default QRCodeScreen;
